import { useMemo } from 'react';
import { useMountingInventoryAPI } from '@elfsight/widget-mounting-inventory';
import { useDelegateEventListening } from './delegate-event-listening';

export interface Util {
  findElement: (selector: string) => Element | null;
  findAllElements: (selector: string) => Element[];
  getWidgetRoot: () => Element | undefined;
  addListener: (
    selector: string,
    eventType: string,
    callback: ListenerCallback
  ) => RemoveListenerFn;
}

export type ListenerCallback = (event: Event) => void;

export type RemoveListenerFn = () => void;

export function useUtil(): Util {
  const mountingInventoryAPI = useMountingInventoryAPI();
  const delegateEventListening = useDelegateEventListening();

  const util: Util = useMemo(() => {
    function getRoots() {
      return mountingInventoryAPI?.getRoots() ?? [];
    }

    function findElement(selector: string) {
      for (const root of getRoots()) {
        const element = root.querySelector(selector);
        if (element) {
          return element;
        }
      }

      return null;
    }

    function findAllElements(selector: string) {
      const elements: Element[] = [];

      for (const root of getRoots()) {
        elements.push(...root.querySelectorAll(selector));
      }

      return elements;
    }

    function getWidgetRoot() {
      return mountingInventoryAPI?.getWidgetRoot();
    }

    function addListener(
      selector: string,
      eventType: string,
      callback: ListenerCallback
    ) {
      return delegateEventListening.addListener(selector, eventType, callback);
    }

    return { findElement, findAllElements, getWidgetRoot, addListener };
  }, [mountingInventoryAPI, delegateEventListening]);

  return util;
}
